import { useEffect, useMemo, useState } from "react";
import { Box, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { fundingSourceApi } from "api/immunization";
import MasterModalShell from "./common/MasterModalShell";
import CodeField from "./common/CodeField";
import { validateCode, validateRequired } from "./common/validation";

const FundingSourceModal = ({ open, onClose, data }) => {
  const recordId = data?._id;
  const isUpdate = Boolean(recordId);

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      code: data?.code || "",
      display: data?.display || "",
      description: data?.description || "",
      isFree: Boolean(data?.isFree),
      governmentReportable: Boolean(data?.governmentReportable),
      requiresSchemeReference: Boolean(data?.requiresSchemeReference),
      sequence: data?.sequence ?? 0,
    });
  }, [open, data]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const validationError = useMemo(
    () =>
      validateCode(form.code, { isUpdate, label: "Funding source code" }) ||
      validateRequired(form.display, "Display name"),
    [form, isUpdate],
  );

  const handleSubmit = async () => {
    if (validationError) return;
    setSaving(true);

    const payload = {
      display: form.display.trim(),
      description: form.description?.trim() || "",
      isFree: form.isFree,
      governmentReportable: form.governmentReportable,
      requiresSchemeReference: form.requiresSchemeReference,
      sequence: Number(form.sequence) || 0,
    };

    try {
      if (isUpdate) {
        await fundingSourceApi.update(recordId, payload);
        toast.success("Funding source updated successfully");
      } else {
        await fundingSourceApi.create({ ...payload, code: form.code.trim() });
        toast.success("Funding source created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} funding source`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterModalShell
      open={open}
      onClose={onClose}
      isUpdate={isUpdate}
      entityLabel="Funding Source"
      saving={saving}
      validationError={validationError}
      onSubmit={handleSubmit}
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        <CodeField
          value={form.code}
          onChange={(value) => set({ code: value })}
          isUpdate={isUpdate}
          label="Funding Source Code"
          helperText='e.g. "government supplied" becomes GOVERNMENT_SUPPLIED'
        />
        <TextField
          label="Display Name"
          required
          fullWidth
          size="small"
          value={form.display || ""}
          onChange={(event) => set({ display: event.target.value })}
        />
        <TextField
          label="Sequence"
          type="number"
          size="small"
          sx={{ minWidth: 120 }}
          value={form.sequence}
          onChange={(event) => set({ sequence: event.target.value })}
        />
      </Box>

      <TextField
        label="Description"
        fullWidth
        size="small"
        multiline
        minRows={2}
        value={form.description || ""}
        onChange={(event) => set({ description: event.target.value })}
      />

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.isFree)}
              onChange={(event) => set({ isFree: event.target.checked })}
            />
          }
          label="Free to patient"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.governmentReportable)}
              onChange={(event) =>
                set({ governmentReportable: event.target.checked })
              }
            />
          }
          label="Government reportable"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.requiresSchemeReference)}
              onChange={(event) =>
                set({ requiresSchemeReference: event.target.checked })
              }
            />
          }
          label="Requires scheme reference"
        />
      </Box>
    </MasterModalShell>
  );
};

export default FundingSourceModal;
